import { Component, OnInit, ComponentFactoryResolver, ViewChild, ViewContainerRef, Type, OnDestroy } from '@angular/core';
import { DiagnosticArticleService } from './service/diagnostic-article.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { AuthService } from 'src/app/services/auth.service';
import { ViewMitchell, ViewVehicle } from 'src/app/models/constant/right';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantStateService } from 'src/app/shared/services/tenant.state.service';
import { DiagnosticArticle } from 'src/app/models/diagnosticArticle';
import { DiagnosticComponent } from 'src/app/models/diagnosticComponent';
import { DiagnosticSection } from 'src/app/models/diagnosticSection';
import { DiagnosticInfo } from 'src/app/models/diagnosticInfo';
import { DiagnosticDataRequest } from 'src/app/models/diagnosticDataRequest';
import { GenericValidators } from 'src/app/shared/generic-validators';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TenantState } from 'src/app/store/state/tenant.state';
import { filter, switchMap, tap, map, takeUntil, catchError, take } from 'rxjs/operators';
import { Observable, Subject, forkJoin, of } from 'rxjs';

import { VehicleCriteria } from 'src/app/models/vehiclecriteria';
import { Tab } from './diagnostic-tab';
import { DiagnosticArticleResponse } from 'src/app/models/diagnosticArticleResponse';
import { FeatureFlagEnum } from 'src/app/shared/feature-flags.enum';
import { GetOBDIICode } from 'src/app/shared/pipes/getOBDIICode';

@Component({
    selector: 'app-diagnostic-article',
    templateUrl: './diagnostic-article.component.html',
    styleUrls: ['./diagnostic-article.component.css'],
    standalone: false
})

export class DiagnosticArticleComponent implements OnInit, OnDestroy {
  @SelectSnapshot(TenantState.getCurrentTenantExternalId) getCurrentTenantExternalId: string;
  @ViewChild('tabContent', { read: ViewContainerRef }) tabContent!: ViewContainerRef;

  selectedIndex: any = 0;
  url: string;
  validators: GenericValidators;
  loading = true;
  length: number;

  diagnosticArticles: DiagnosticArticle[];
  diagnosticComponents: DiagnosticComponent[];
  diagnosticSections: DiagnosticSection[];
  diagnosticComponentSections: DiagnosticSection[];

  private tenantId: string;
  private destroy$ = new Subject();
  public vin: string;
  public protocol: string;
  public sourceAddress: number;
  public faultCode: number;
  public failureMode: string;
  public failureCode: number;
  public country = 'us';
  canNavigateToVehicle = false;
  public model: string;
  public make: string;
  public year: string;
  public engine: string;
  public brakeSystem: string;
  public vehicleCriteria: VehicleCriteria;
  public diagnosticInfo: DiagnosticInfo;
  public sectionIds: string[];
  public componentIds: string[];
  public componentSectionIds: string[];
  public tabs: Tab[] = [];

  public connector: DiagnosticSection[];
  public location: DiagnosticSection[];
  public operation: DiagnosticSection[];
  public repair: DiagnosticSection[];
  public spec: DiagnosticSection[];
  public testing: DiagnosticSection[];
  public wire: DiagnosticSection[];
  public noData: DiagnosticSection[];
  codeDescription: string = '';
  sectionDataRequest: DiagnosticDataRequest;
  componentDataRequest: DiagnosticDataRequest;
  componentSectionDataRequest: DiagnosticDataRequest;
  apiMessage: string = '';
  buildingCase: boolean = false;
  obdiiCode: string = '';
  operationTitle: string = '@description';
  locationTitle: string = '@locationTitle';
  repairTitle: string = '@repairTitle';
  specTitle: string = '@specTitle';
  testingTitle: string = '@testingTitle';
  wireTitle: string = '@wireTitle';
  noDataTitle: string = '@noDataTitle';

  diagnosticArticles$: Observable<boolean>;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private featureFlagService: FeatureFlagService,
    private diagnosticArticleService: DiagnosticArticleService,
    private authorService: AuthService,
    private authorizationService: AuthorizationService,
    public translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private tenantStateService: TenantStateService) { }

  ngOnInit(): void {
    this.canNavigateToVehicle = this.authorizationService.hasRight(
      this.authorService.user.profile,
      ViewMitchell);

    this.diagnosticArticles$ = this.featureFlagService.getFeatureFlagValue(FeatureFlagEnum.DiagnosticArticles);
    this.tenantId = this.getCurrentTenantExternalId;

    const params = this.route.snapshot.params;
    const queryParams = this.route.snapshot.queryParams;

    this.vin = params.vin ?? this.vin;
    this.protocol = params.protocol ? this.checkProtocol(params.protocol.toUpperCase().trim()) : this.protocol;
    this.sourceAddress = params.sourceAddress ?? this.sourceAddress;
    this.faultCode = params.faultCode ?? this.faultCode;
    this.failureCode = params.failureCode ?? this.failureCode;
    this.failureMode = queryParams.faultType ?? params.failureMode ?? this.failureMode;
    this.country = params.country ?? this.country;
    this.codeDescription = queryParams.subject ?? this.codeDescription;

    this.getData().subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getSectionData(): Observable<DiagnosticSection[]> {
    return this.diagnosticArticleService.getSectionData(this.sectionDataRequest).pipe(
      filter((sections: DiagnosticSection[]) => sections != undefined),
      tap(sections => this.diagnosticSections = [...sections]));
  }

  getComponentData(): Observable<void> {
    return this.diagnosticArticleService.getComponentData(this.componentDataRequest).pipe(
      tap((components: DiagnosticComponent[]) => {
        this.diagnosticComponents = [...components];
        this.componentSectionIds = this.diagnosticComponents.length === 0 ? [] : this.diagnosticComponents[0].sections;
        this.componentSectionDataRequest = new DiagnosticDataRequest(this.vin, this.componentSectionIds);
      }),
      switchMap((components: DiagnosticComponent[]) => this.diagnosticArticleService.getSectionData(this.componentSectionDataRequest)),
      filter((sections: DiagnosticSection[]) => sections != undefined),
      map(section => {
        this.diagnosticComponentSections = [...section];
        if (this.diagnosticComponentSections) {
          if (this.diagnosticSections) {
            for (var i = 0; i < this.diagnosticSections.length; i++) {
              this.diagnosticComponentSections.push(this.diagnosticSections[i]);
            }
          }

          this.noData = this.findNoData(this.diagnosticComponentSections);

          if (this.noData.length === 0) {
            this.connector = this.findConnector(this.diagnosticComponentSections);
            this.location = this.findLocation(this.diagnosticComponentSections);
            this.operation = this.findOperation(this.diagnosticComponentSections);
            this.repair = this.findRepair(this.diagnosticComponentSections);
            this.spec = this.findSpecification(this.diagnosticComponentSections);
            this.testing = this.findTesting(this.diagnosticComponentSections);
            this.wire = this.findWiring(this.diagnosticComponentSections);
          }
        }
        else {
          if (this.diagnosticSections) {
            for (var i = 0; i < this.diagnosticSections.length; i++) {
              this.diagnosticComponentSections.push(this.diagnosticSections[i]);
            }
          }
        }
      }));
  }

  getData(): Observable<boolean> {
    this.buildingCase = true;
    const article$ = this.diagnosticArticleService.getArticleData(this.vin, this.protocol.toLowerCase(), this.sourceAddress, this.faultCode, this.failureCode, this.failureMode, this.country)
      .pipe(
        catchError(_ => {
          console.log("Attempting to build article from Mitchel1");
          this.apiMessage = this.translate.instant('@buildingArticleMessage'); //'Building article, try again in a few moments';
          return this.apiMessage;
        }),
        tap((response: DiagnosticArticleResponse) => {
          this.apiMessage = this.displayUserMessage(response.message);
          this.obdiiCode = new GetOBDIICode().transform(this.sourceAddress.toString(), this.faultCode.toString())
          if (this.apiMessage.length == 0) {
            this.diagnosticArticles = [...response.diagnosticArticles];
            this.vehicleCriteria = this.diagnosticArticles[0].vehicleCriteria;
            this.diagnosticInfo = this.diagnosticArticles[0].diagnosticInfo;
            this.sectionIds = this.diagnosticArticles[0].sectionIds;
            this.componentIds = this.diagnosticArticles[0].componentIds;
            this.sectionDataRequest = new DiagnosticDataRequest(this.vin, this.sectionIds);
            this.componentDataRequest = new DiagnosticDataRequest(this.vin, this.componentIds);
          }
        }),
        filter(() => this.apiMessage.length === 0),
        switchMap(() => forkJoin([this.getSectionData(), this.getComponentData()])),
        takeUntil(this.destroy$));

    return article$.pipe(take(1), filter(sections => sections.length < 2), map(() => this.buildingCase = false));
  }

  findConnector(sections: DiagnosticSection[]): DiagnosticSection[] {
    var connector = this.findSection('connector', sections);

    if (connector.length > 0) {
      if (!this.tabs.some(tab => tab.title === 'Connector')) {
        this.tabs.push({ title: 'Connector', content: connector });
      }
    }
    return connector;
  }

  findOperation(sections: DiagnosticSection[]): DiagnosticSection[] {
    var ops = this.findSection("operation", sections);

    if (ops.length > 0) {
      if (!this.tabs.some(tab => tab.title === this.operationTitle)) {
        this.tabs.push({ title: this.operationTitle, content: ops });
      }
    }
    return ops;
  }

  findLocation(sections: DiagnosticSection[]): DiagnosticSection[] {
    var loc = this.findSection("location", sections);

    if (loc.length > 0) {
      if (!this.tabs.some(tab => tab.title === 'Location')) {
        this.tabs.push({ title: this.locationTitle, content: loc });
      }
    }
    return loc;
  }

  findNoData(sections: DiagnosticSection[]): DiagnosticSection[] {
    var rep = this.findSection('No Information Found', sections);

    if (rep.length > 0) {
      if (!this.tabs.some(tab => tab.title === 'No Information Found')) {
        this.tabs.push({ title: this.noDataTitle, content: rep });
      }
    }
    return rep;
  }

  findRepair(sections: DiagnosticSection[]): DiagnosticSection[] {
    var rep = this.findSection("repair", sections);

    if (rep.length > 0) {
      if (!this.tabs.some(tab => tab.title === 'Repair')) {
        this.tabs.push({ title: this.repairTitle, content: rep });
      }
    }
    return rep;
  }

  findSpecification(sections: DiagnosticSection[]): DiagnosticSection[] {
    var spec = this.findSection("specifications", sections);

    if (spec.length > 0) {
      if (!this.tabs.some(tab => tab.title === 'Specifications')) {
        this.tabs.push({ title: this.specTitle, content: spec });
      }
    }
    return spec;
  }

  findTesting(sections: DiagnosticSection[]): DiagnosticSection[] {
    var test = this.findSection("testing", sections);

    if (test.length > 0) {
      if (!this.tabs.some(tab => tab.title === 'Testing')) {
        this.tabs.push({ title: this.testingTitle, content: test });
      }
    }
    return test;
  }

  findWiring(sections: DiagnosticSection[]): DiagnosticSection[] {
    // Create separate arrays for each wiring type
    const systemWiring = sections.filter(dia => dia.categories.includes('wiringdiagram_system'));
    const generalWiring = sections.filter(dia => dia.categories.includes('wiringdiagram'));
    const noConnectorsWiring = sections.filter(dia => dia.categories.includes('wiringdiagram_noconnectors'));
    const subcircuitWiring = sections.filter(dia => dia.categories.includes('wiringdiagram_subcircuit'));

    // Combine all wiring arrays into one array for processing
    const wires = [...systemWiring, ...generalWiring, ...noConnectorsWiring, ...subcircuitWiring];

    // Check if the wires array has elements and the 'Wiring' tab is not already present
    if (wires.length > 0) {
      if (!this.tabs.some(tab => tab.title === 'Wiring')) {
        this.tabs.push({ title: this.wireTitle, content: wires });
      }
    }

    return wires;
  }

  findSection(sectionName: string, sections: DiagnosticSection[]) {
    var sectionArray = new Array<DiagnosticSection>();
    for (var dia of sections) {
      if (dia.categories.includes(sectionName)) {
        sectionArray.push(dia);
      }
    }

    return sectionArray;
  }

  displayUserMessage(message: string): string {
    switch (message) {
      case '1':
        return this.translate.instant("@buildingArticleMessage");
      case '2':
        return this.translate.instant("@articleRequestErrorMessage");
      case '3':
        return this.translate.instant("@notFound");
      case '':
        return '';
      default:
        return this.translate.instant("@articleRequestErrorMessage");
    }
  }

  async loadComponent(title: string, content: DiagnosticSection[]) {
    this.tabContent.clear();  // Clear previous content
    const component = await this.getComponentByTitle(title);
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = this.tabContent.createComponent(componentFactory);
    (componentRef.instance as any).data = content;  // Pass content data to the component
  }

  checkProtocol(value: string): string {
    if(value === 'J1939') {
      return 'J1939';
    }

    if (this.isJ1708OrJ1587(value)) {
      return 'J1708';
    }

    if (this.isOBDIIOrODBII(value)) {
      return 'OBDII'
    }

    return '';
  }

  isJ1708OrJ1587(value: string): boolean {
    return value === 'J1708' || value === 'J1587';
  }

  isOBDIIOrODBII(value: string): boolean {
    return value === 'OBDII' || value === 'ODBII';
  }

  private async getComponentByTitle(title: string): Promise<Type<any>> {
    switch (title) {
      case this.locationTitle:
      case this.repairTitle:
      case this.testingTitle:
      case this.operationTitle:
        const { OperationComponent } = await import('./operation/operation.component');
        return OperationComponent;
      case this.specTitle:
        const { SpecificationsComponent } = await import('./specifications/specifications.component');
        return SpecificationsComponent;
      case this.wireTitle:
        const { WiringComponent } = await import('./wiring/wiring.component');
        return WiringComponent;
      case this.noDataTitle:
        const { NoDataComponent } = await import('./no-data/no-data-component');
        return NoDataComponent;
      default:
        throw new Error(`Unknown component: ${title}`);
    }
  }

  onTabChange(event: any) {
    const selectedIndex = event.index;
    const selectedTab = this.tabs[selectedIndex];
    this.loadComponent(selectedTab.title, selectedTab.content);
  }
}
